import { supabase } from '../lib/supabaseClient';

/**
 * Service to handle remote logout requests when users are locked out
 * Allows users to force logout from another device via email confirmation
 */
export class RemoteLogoutService {
  /**
   * Request a remote logout - sends confirmation code to user's email
   */
  async requestRemoteLogout(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('[RemoteLogout] Requesting logout for email:', email);

      // Call RPC function - it returns code and userName directly
      const { data, error } = await supabase.rpc('request_remote_logout', {
        user_email: email
      });

      console.log('[RemoteLogout] Raw RPC response:', { data, error });

      if (error) {
        console.error('[RemoteLogout] RPC error:', error);
        return { success: false, error: error.message };
      }

      if (!data) {
        console.error('[RemoteLogout] RPC returned null/undefined - function may not be deployed');
        return { success: false, error: 'Database function not available. Please contact support.' };
      }

      if (!data.success) {
        console.error('[RemoteLogout] RPC returned error:', data?.error);
        return { success: false, error: data?.error || 'Failed to generate confirmation code' };
      }

      const code = data.code;
      const userName = data.userName || 'User';

      console.log('[RemoteLogout] ✅ Code generated:', code.substring(0, 2) + '****');

      // Send email via SendGrid (same pattern as verification email)
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://noorai-api.vercel.app';
      
      console.log('[RemoteLogout] Sending confirmation email via SendGrid to:', API_BASE_URL);
      const response = await fetch(`${API_BASE_URL}/send-remote-logout-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code, userName }),
      });

      if (!response.ok) {
        console.error('[RemoteLogout] Email send failed:', response.statusText);
        // Don't fail the whole request - code is in DB
        console.warn('[RemoteLogout] Email failed but code is stored in database');
      } else {
        console.log('[RemoteLogout] ✅ Confirmation email sent via SendGrid');
      }

      return { success: true };
    } catch (error: any) {
      console.error('[RemoteLogout] Error:', error);
      return { success: false, error: error.message || 'Failed to request logout' };
    }
  }

  /**
   * Verify confirmation code and logout all devices
   */
  async confirmRemoteLogout(email: string, code: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('[RemoteLogout] Confirming logout with code');

      // Call RPC function to verify code and logout all sessions
      const { data, error } = await supabase.rpc('confirm_remote_logout', {
        user_email: email,
        confirmation_code: code
      });

      if (error) {
        console.error('[RemoteLogout] Confirmation error:', error);
        return { success: false, error: error.message };
      }

      if (!data) {
        return { success: false, error: 'Invalid or expired code' };
      }

      console.log('[RemoteLogout] ✅ Remote logout confirmed, all sessions terminated');
      return { success: true };
    } catch (error: any) {
      console.error('[RemoteLogout] Error:', error);
      return { success: false, error: error.message || 'Failed to confirm logout' };
    }
  }
}

export const remoteLogoutService = new RemoteLogoutService();
